import type { Theme } from '@mui/material/styles';

import { ProjectInfoT } from '../components/project/dashboard/dashboard-context';

import * as GQL from '~/graphql/generated/client';

/**
 * @deprecated com.fairly.ai.projectinfo is no longer auto-attached to new projects.
 * Inherent risk classification now comes from screeningProfile.classification.
 * This function remains only for backward compatibility with existing projects.
 * For new code, check screeningProfile or setup-usecase assessment instead.
 */
export function isProjectInfoComplete(assessments: GQL.AssessmentFragment[]) {
  return assessments.some(
    (assessment) =>
      assessment.name.includes('com.fairly.ai.projectinfo') &&
      assessment.completionPercent === 100
  );
}

export function isAssessmentEmpty(assessments: GQL.AssessmentFragment[]) {
  return assessments.length === 0;
}

export const getProjectInfoAnswer = (
  identifier: string,
  projectInfo: ProjectInfoT[]
) => projectInfo.find((e) => e.identifier === identifier)?.answer;

interface RiskStatusIconProps {
  riskStatus: GQL.AssessmentRiskStatusType | null | undefined;
}

export const hasTestScoreAnswerTypeInAnswers = (
  answers: GQL.AnswerFragment[],
  controls: GQL.Control[]
): boolean => {
  return answers.some((answer) => {
    const controlDef = controls.find(
      (control) => control.identifier === answer.controlIdentifier
    );
    return controlDef?.answerType === 'TEST_SCORE';
  });
};

export const getColorBasedOnScore = (
  theme: Theme,
  level: GQL.AssessmentRiskStatusType | null | undefined
) => {
  switch (level) {
    case GQL.AssessmentRiskStatusType.High:
      return theme.palette.error.main;

    case GQL.AssessmentRiskStatusType.Medium:
      return theme.palette.warning.main;

    case GQL.AssessmentRiskStatusType.Low:
      return theme.palette.info.main;
    default:
      return theme.palette.error.main;
  }
};

/**
 * Formats a date in short format (e.g., "Jan 15, 2024")
 * @param dateValue - Date string, timestamp, or Date object
 * @returns Formatted date string in "MMM DD, YYYY" format, or empty string if invalid
 */
export const formatDate = (
  dateValue: string | number | Date | null | undefined
): string => {
  if (!dateValue) {
    return '';
  }

  try {
    const date = new Date(dateValue);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Formats a time in 12-hour format (e.g., "02:30 PM")
 * @param dateValue - Date string, timestamp, or Date object
 * @returns Formatted time string in "HH:MM AM/PM" format, or empty string if invalid
 */
export const formatTime = (
  dateValue: string | number | Date | null | undefined
): string => {
  if (!dateValue) {
    return '';
  }

  try {
    const date = new Date(dateValue);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
};

/**
 * Formats a date and time together (e.g., "Jan 15, 2024 at 02:30 PM")
 * @param dateValue - Date string, timestamp, or Date object
 * @returns Formatted datetime string, or empty string if invalid
 */
export const formatDateTime = (
  dateValue: string | number | Date | null | undefined
): string => {
  if (!dateValue) {
    return '';
  }

  const formattedDate = formatDate(dateValue);
  const formattedTime = formatTime(dateValue);

  if (!formattedDate || !formattedTime) {
    return '';
  }

  return `${formattedDate} at ${formattedTime}`;
};

/**
 * Maps a risk status to a human-readable compliance label.
 * Used for display purposes where the old AlignmentStatus labels are needed.
 */
export function riskStatusToComplianceLabel(
  status: GQL.AssessmentRiskStatusType | null | undefined
): string {
  switch (status) {
    case GQL.AssessmentRiskStatusType.Low:
      return 'Compliant';
    case GQL.AssessmentRiskStatusType.Medium:
      return 'Partially Compliant';
    case GQL.AssessmentRiskStatusType.High:
      return 'Not Compliant';
    case GQL.AssessmentRiskStatusType.Unavailable:
      return 'Not Available';
    default:
      return 'Unknown';
  }
}
